import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { ParentFamilyContextService } from '../../parent-family/parent-family-context.service';

@Injectable({ providedIn: 'root' })
export class HomeDataService {
  #http = inject(HttpClient);
  #family = inject(ParentFamilyContextService);
  #queryClient = inject(QueryClient);

  readonly dutiesFetchEnabled = signal(false);
  readonly coursesSummaryFetchEnabled = signal(false);
  readonly coursesFetchEnabled = signal(false);

  readonly parentDutiesQuery = injectQuery(() => {
    const kidId = this.#family.selectedChildId()?.trim() ?? '';
    const hasKidScope = !!kidId;
    return {
      queryKey: ['parentDuties', kidId || 'none'],
      enabled: this.dutiesFetchEnabled() && hasKidScope,
      staleTime: 60_000,
      retry: 1,
      queryFn: () => {
        const params = new HttpParams().set('kidId', kidId);
        return lastValueFrom(
          this.#http
            .get<ChallengeGen.Dutie[]>(`${environment.baseUrl}/duties/parent`, {
              params,
            })
            .pipe(take(1)),
        );
      },
    };
  });

  /** Lightweight bucket counts for the courses tab badge. */
  readonly coursesSummaryQuery = injectQuery(() => ({
    queryKey: ['courseAvailableSummary', 'parent'],
    enabled: this.coursesSummaryFetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.CourseGroupedSummaryRes>(
            `${environment.baseUrl}/courses/available-groupped`,
          )
          .pipe(take(1)),
      ),
  }));

  /** Lightweight course rows when the courses tab is opened. */
  readonly coursesQuery = injectQuery(() => ({
    queryKey: ['courseAvailableLight', 'parent'],
    enabled: this.coursesFetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.AvailableCoursesLightRes>(
            `${environment.baseUrl}/courses/available-light`,
          )
          .pipe(take(1)),
      ),
  }));

  readonly parentDuties = computed(() => {
    if (!this.#family.selectedChildId()?.trim()) {
      return [];
    }
    return this.parentDutiesQuery.data() ?? [];
  });
  readonly pendingReviewDuties = computed(() =>
    this.parentDuties().filter((duty) => duty.completed && !duty.confirmed),
  );
  readonly parentDutiesLoading = computed(() => {
    if (!this.#family.selectedChildId()?.trim()) {
      return false;
    }
    return this.parentDutiesQuery.isLoading();
  });

  readonly courses = computed(() =>
    (this.coursesQuery.data()?.data ?? []).map((light) =>
      this.#mapLightCourse(light),
    ),
  );
  readonly coursesLoading = computed(() => this.coursesQuery.isLoading());

  readonly coursesCount = computed(() => {
    if (this.coursesFetchEnabled() && !this.coursesLoading()) {
      return this.courses().length;
    }
    return this.#sumCourseBucketCounts(this.coursesSummaryQuery.data()?.data);
  });

  /** Load duties for the assignments tab (badges + board). */
  prefetchDuties(): void {
    if (this.dutiesFetchEnabled()) {
      return;
    }
    this.dutiesFetchEnabled.set(true);
  }

  /** Load course counts for the tab badge (small payload). */
  prefetchCoursesSummary(): void {
    if (this.coursesSummaryFetchEnabled()) {
      return;
    }
    this.coursesSummaryFetchEnabled.set(true);
  }

  /** Load course list when the courses tab is opened. */
  prefetchCourses(): void {
    if (this.coursesFetchEnabled()) {
      return;
    }
    this.coursesFetchEnabled.set(true);
  }

  invalidateParentDuties(): void {
    this.#queryClient.invalidateQueries({ queryKey: ['parentDuties'] });
  }

  #sumCourseBucketCounts(
    buckets?: ChallengeGen.CourseGroupedSummaryBuckets,
  ): number {
    if (!buckets) {
      return 0;
    }
    return (
      (buckets.valvur?.courseCount ?? 0) +
      (buckets.parent?.courseCount ?? 0) +
      (buckets.teacher?.courseCount ?? 0)
    );
  }

  #mapLightCourse(light: ChallengeGen.AvailableCourseLight): ChallengeGen.Course {
    const hasProgress =
      (light.completedTaskCount ?? 0) > 0 || (light.completionPercent ?? 0) > 0;
    return {
      id: light.id,
      courseName: light.courseName,
      courseDescription: light.courseDescription,
      category: light.category,
      totalPoints: light.totalPoints,
      courseImageUrl: light.courseImageUrl,
      taskCount: light.taskCount,
      completedTaskCount: light.completedTaskCount,
      completionPercent: light.completionPercent,
      courseJoined: hasProgress,
    } as unknown as ChallengeGen.Course;
  }
}
